
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Message, Space, Tag } from '@bedrock/components';
import closingReportTableModal from './ClosingReportTableModal';
import { ChevronRight, Edit } from '@bedrock/icons-react';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { $sType } from '#utils';
import { apiService } from '#services';

type ClosingReportBtnProps = {
    value?: any;
    onChange?: (value: any) => void;
    view?: boolean;
    cooperateAccountId?: number;
    detail: any;
}

// 结案明细按钮组件，支持查看和编辑结案明细
const ClosingReportBtn = forwardRef((props: ClosingReportBtnProps, ref) => {
    const { detail, view = false, cooperateAccountId } = props;
    const [localValue, setLocalValue] = useControllableValue(props);
    // 表格列配置
    const [columns, setColumns] = useState([]);
    // 报价数据
    const [quoteData, setQuoteData] = useState([]);
    // 加载状态
    const [loading, setLoading] = useState(false);

    const [saveTime, setSaveTime] = useState('');

    // 初始化结案明细数据，将settleReports与quoteData进行匹配
    const initQuoteDataFn = useMemoizedFn((quoteData, settleReports) => {
        let quoteKeyMap = {};
        const reports = quoteData.reduce((acc, item) => {
            // 根据quoteKey在settle_reports中找到对应的report
            const report = settleReports?.find((report) => report.quote_key === item.quoteKey);
            if (report && !quoteKeyMap[item.quoteKey]) {
                acc.push(report);
                quoteKeyMap[item.quoteKey] = report;
            } else {
                acc.push({});
            }
            return acc;
        }, []);

        // 通知父组件数据变更
        setLocalValue(reports);
    });

    // 初始化数据，获取结案明细相关数据
    const initDataFn = useMemoizedFn(async () => {
        if (!detail?.id || !detail?.demand_suppliers?.[0]?.id) return;
        setLoading(true);
        try {
            // 根据$sType选择不同的API
            const quoteSettleReportsApi = $sType === 'inner' ? apiService.demandQueryDemandSupplierQuoteSettleReports : apiService.outsourceDemandQueryDemandSupplierQuoteSettleReports;
            const res = await quoteSettleReportsApi({
                demand_supplier_id: detail.demand_suppliers?.[0]?.id,
            });
            const { columns, quote_data, settle_reports } = res?.data || {};
            let reportList = settle_reports;
            setColumns(columns || []);
            let quoteData = quote_data;
            // 如果有合作内容ID，过滤对应的报价数据
            if (cooperateAccountId) {
                quoteData = quote_data.filter((item) => item.cooperateAccountId == cooperateAccountId);
            }
            setQuoteData(quoteData);
            // 外部编辑模式下，优先获取暂存的结构化结案报告数据
            if (!view && $sType === 'outer') {
                const res = await apiService.outsourceDemandGetSavedStructuredSettleReportsData({
                    demand_id: detail.id,
                });
                const { quote_settle_reports, saved_time } = res?.data || {};
                if (quote_settle_reports?.length) {
                    reportList = quote_settle_reports;
                    setSaveTime(saved_time);
                }
            }
            // 初始化结案明细
            initQuoteDataFn(quoteData, reportList);
            setLoading(false);  
        } catch (error) {
            setLoading(false);
            throw error;
        }
    });

    // 展示结案明细弹窗
    const showModalFn = useMemoizedFn(async () => {
        closingReportTableModal.show({
            detail,
            reportList: localValue,
            view: view,
            cooperateAccountId,
            columns: columns || [],
            quoteData: quoteData || [],
            onOk: (data: any) => {
                setLocalValue(data)
            }
        })
        // if (saveTime) {
        //     Message.info('已自动恢复上次 ' + saveTime + ' 暂存的内容');
        //     setSaveTime('');
        // }
    })

    // 判断每个quoteKey对应的report是否都已填写说明或者附件
    const valueReady = useMemo(() => {
        if (!localValue?.length) return false;
        const quoteKeyMap = localValue?.reduce((acc, item) => {
            acc[item.quote_key] = item;
            return acc;
        }, {}) || {};
        return quoteData?.every((item) => {
            const report = quoteKeyMap[item.quoteKey];
            return report?.content || !!report?.attachments?.length;
        });
    }, [localValue, quoteData]);

    // 暴露校验方法给父组件
    useImperativeHandle(ref, () => ({
        validateFields: () => {
            if (view) {
                // 只读模式直接通过
                return true;
            } else {
                if (valueReady) {
                    return Promise.resolve(true);
                } else {
                    return Promise.reject('请完善结案明细');
                }
            }
        }
    }));

    // 非查看模式下，初始化数据
    useEffect(() => {
        !view && initDataFn();
    }, [detail, view, cooperateAccountId]);

    // 如果未提交结构化结案报告且为查看模式，显示占位符
    if (!detail?.submitted_structured_settle_report && view) {
        return '--';
    }

    // 渲染按钮和状态标签
    return <Space size={8}>
        <Button
            onClick={async () => {
                // 查看模式下每次点击都重新拉取数据
                if (view) {
                    await initDataFn()
                }
                // 弹窗异步打开
                setTimeout(() => {
                    showModalFn()
                }, 1)
            }}
            loading={loading}
            type="checked"
            icon={!view ? <Edit /> : null}
            suffixIcon={view ? <ChevronRight /> : null}
            size="medium"
        >
            {view ? '查看结案明细' + (!cooperateAccountId ? '报告' : '') : '填写结案明细'}
        </Button>
        {/* 编辑模式下显示已提交/未提交状态 */}
        {!view && <Tag size="small" color={valueReady ? 'light-green' : 'light-orange'} text={valueReady ? '已填写' : '未填写'} />}
    </Space>
});

export default ClosingReportBtn;
